import { configure, extend, setInteractionMode } from 'vee-validate'
import * as rules from 'vee-validate/dist/rules'
import freeEmailDomains from 'free-email-domains'
import utils from '@/components/tools/utils'

setInteractionMode('aggressive')

Object.keys(rules).forEach((rule) => {
  // eslint-disable-next-line import/namespace
  extend(rule, rules[rule])
})

function formatFileSize (size, units = ['Byte', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'], delimiter = ' ') {
  const threshold = 1024
  size = size * threshold
  const i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(threshold))
  return (size / Math.pow(threshold, i)).toFixed(2) * 1 + delimiter + units[i]
}

export default function ({ app }) {
  extend('description_min', {
    validate (value, args) {
      // 1. Handle null / undefined / empty string early
      if (!value || value === '') {
        return false
      }

      // 2. Strip HTML & normalize whitespace
      const text = value
        .replace(/<[^>]*>/g, '')
        .replace(/\u00A0/g, ' ') // nbsp (important for editors)
        .replace(/\s+/g, ' ')
        .trim()

      // 3. Enforce minimum length
      return text.length >= 10
    },
    message: (_, values) => app.i18n.t('validation.description_min')
  })

  extend('required', {
    ...rules.required,
    validate: (value, args) => {
      return !(value === undefined || value === null || value === '' || value.length === 0 || value === false)
    }
  })

  extend('required_boolean', {
    validate (value, args) {
      return value === true || value === false
    },
    message: (_, values) => app.i18n.t('validation.required', values),
    computesRequired: true
  })

  extend('required_true', {
    validate (value, args) {
      return value === true
    },
    message: (_, values) => app.i18n.t('validation.required', values),
    computesRequired: true
  })

  extend('size', {
    ...rules.size,
    message: (_, values) => {
      return `${values._field_} muss kleiner als ${formatFileSize(values.size)} sein`
    }
  })

  extend('image_or_pdf', {
    ...rules.mimes,
    message: (_, values) => {
      return `${values._field_} muss ein Bild oder eine PDF-Datei sein`
    }
  })

  extend('image', {
    validate (fileOrFiles) {
      const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles]

      return files.every(
        file =>
          /image\/jpeg$/i.test(file.type) ||
          /image\/jpg$/i.test(file.type) ||
          /image\/png$/i.test(file.type) ||
          /image\/heic$/i.test(file.type) ||
          /image\/heif$/i.test(file.type) ||
          /\.heic$/i.test(file.name) ||
          /\.heif$/i.test(file.name)
      )
    },
    message: (_, values) => {
      return `${values._field_} muss eine Bild-Datei sein.`
    }
  })

  extend('file', {
    validate (value, args) {
      return value.length !== 0
    },
    message: (_, values) => app.i18n.t('validation.required', values)
  })
  extend('telephone_personal_data', {
    validate (value, args) {
      const result = value.match(/^(\+|0|00)[1-9][0-9 \-\\(\\)\\.]{4,32}$/)
      return result != null
    },
    message: (_, values) => app.i18n.t('validation.telephone', values)
  })

  extend('zip_code', {
    validate (value, args) {
      const result = value.match(/^([0-9]{4,5}$)|^([0-9]{4}\s*[A-Za-z]{2}$)$/)
      return result != null
    },
    message: (_, values) => app.i18n.t('validation.zip_code', values)
  })

  extend('zip_code_de', {
    validate (value, args) {
      const result = value.match(/^[0-9]{5}$/)
      return result != null
    },
    message: (_, values) => app.i18n.t('validation.zip_code', values)
  })
  extend('zip_code_nl', {
    validate (value, args) {
      const result = value.match(/^[0-9]{4}\s*[A-Za-z]{2}$/)
      return result != null
    },
    message: (_, values) => app.i18n.t('validation.zip_code', values)
  })

  extend('telephone', {
    validate (value, args) {
      const result = value.match(/^(\+)[ 0-9]+$/)
      return result != null
    },
    message: (_, values) => app.i18n.t('validation.telephone', values)
  })

  extend('dutchLicensePlate', {
    validate (value, args) {
      const result = value.match(/^[0-9A-Z-]+$/)
      return result != null
    },
    message: (_, values) => app.i18n.t('validation.dutchLicensePlate', values)
  })

  extend('importer_link', {
    validate (value, args) {
      // https://suchen.mobile.de/fahrzeuge/details.html?id=340931672
      // https://suchen.mobile.de/wohnwagen-inserat/bürstner-ixeo-time-it-586-automatik-177-ps-solingen/335797124.html
      // https://m.mobile.de/wohnwagen-inserat/dethleffs-esprit-comfort-i-7090-2-dopp-boden-360-kamera-parchim/340842402.html

      // https://www.ebay-kleinanzeigen.de/s-anzeige/wohnwagen-wilk/2297580423-220-1097

      const a = value.match(/https:\/\/suchen\.mobile\.de\/fahrzeuge\/details\.html\?id=[0-9]+/)
      const b = value.match(/https:\/\/suchen.mobile.de\/wohnwagen-inserat\/[^/]+\/[0-9]+.html/)
      const c = value.match(/https:\/\/m.mobile.de\/wohnwagen-inserat\/[^/]+\/[0-9]+.html/)
      const d = value.match(/https:\/\/www\.kleinanzeigen\.de\/s-anzeige\//)
      const e = value.match(/[0-9]+/)

      return a != null || b != null || c != null || d != null || e != null
    },
    message: (_, values) => app.i18n.t('validation.importer_link', values)
  })

  extend('license_plate_nl', {
    validate (value, args) {
      // TODO
      return true
    },
    message: (_, values) => app.i18n.t('validation.license_plate_nl', values)
  })

  extend('free_email_provider', {
    validate (value, args) {
      if (value === null || value === undefined) {
        return false
      }

      return freeEmailDomains.includes(value)
    },
    message: (_, values) => app.i18n.t('validation.email_provider', values)
  })

  extend('password', {
    validate (value) {
      return /[A-Z]/.test(value) && /[a-z]/.test(value) && /\d/.test(value)
    }
  })
  extend('no_umlaute', {
    validate (value) {
      return !/[äöüÄÖÜ]/.test(value)
    },
    message: (_, values) => app.i18n.t('validation.no_umlaute', values)

  })

  extend('min_target_price', {
    validate (value, args) {
      const price = parseFloat(value)
      if (isNaN(price)) {
        return false
      }

      if (price < args[0]) {
        return false
      }
      return true
    },

    message: (_, values) => {
      const value = parseFloat(values._value_)
      if (isNaN(value)) {
        return app.i18n.t('validation.numeric')
      }

      return app.i18n.t('validation.min_target_price', [utils.formatCurrency(values._value_)])
    }
  })

  configure({

    defaultMessage: (field, values) => {
      values._field_ = field // app.i18n.t(`${field}`);
      return app.i18n.t(`validation.${values._rule_}`, values)
    }
  })
}
